import type { Tag } from '~/api/models/Tag';
import { FavoriteFile } from '~/composables/files/useFavorites';
import { BaseModel } from './BaseModel';

export interface EggFirewallRuleEntry {
    protocol?: string;
    action?: string;
    destination_port?: string;
}

export interface EggFirewallFilterEntry {
    filter: string;
    settings: Record<string, unknown>;
}

export interface EggFirewallInfralyConfig {
    rules: EggFirewallRuleEntry[];
    filters: EggFirewallFilterEntry[];
}

export interface EggFirewallConfig {
    infraly?: EggFirewallInfralyConfig;
}

// One extra allocation a server of this egg is granted. The port is either an
// offset from the primary allocation, or any free port (the highest available on
// the IP, so it doesn't consume the low contiguous ports primaries use). It is
// optionally bound to an egg startup variable (by env_variable name; null =
// reserve the port only). A server template can override a given additional port
// with a fixed range (see AdditionalPortRange).
export interface AdditionalPort {
    variable: string | null;
    type: 'offset' | 'any_free';
    offset?: number;
}

// A server template's range override for one of an egg's additional ports. It
// targets the egg port by bound variable name (else by position) and replaces
// that port's offset/any_free rule with a fixed [from, to] range.
export interface AdditionalPortRange {
    variable: string | null;
    index: number;
    from: number;
    to: number;
}

export type AdditionalPortFailMode = 'continue' | 'stop';

export class Egg extends BaseModel {
    public name = '';
    // What the daemon queries a server with: a gamedig game type (`rust`) or a
    // `protocol-<name>` pseudo-type. Drives ONLY querying -- never feature gating,
    // which is tag-based. Empty falls back to the game tag's default on the backend.
    public gamedigIdentifier = '';

    public thumbnail: string | null = null;
    public canUpdate = false;
    public identifier = '';
    public favoriteFiles?: FavoriteFile[];

    // Switch-game requirement status (set when listing switchable games).
    public meetsMinimum = true;
    public meetsRecommended = true;
    public requiresNodeChange = false;
    public requirements: { minimum: Record<string, number | null>; recommended: Record<string, number | null> } | null = null;

    // Admin only props
    public id = -1;
    public legacyId: number | null = null;
    public description = '';
    public dockerImages: Record<string, string> = {};
    public startup = '';
    public configFrom?: number;
    public configLogs = '';
    public configFiles = '';
    public configStartup = '';
    public configStop = '';
    public customConfig = '';
    public wispignore = '';
    public firewallConfig: EggFirewallConfig | null = null;

    public scriptInstall = '';
    public scriptIsPrivileged = false;
    public scriptEntry = '';
    public scriptContainer = '';
    public copyScriptFrom?: number;

    public scriptUpdate = '';
    public scriptUpdateIsPrivileged = false;
    public scriptUpdateEntry = '';
    public scriptUpdateContainer = '';
    public copyUpdateScriptFrom?: number;

    public nestId = -1;
    public serversCount?: number;

    // Admin game-switching policy (Game Switching tab + switch-groups board).
    public switchDisabled = false;
    public switchGroupIds: string[] = [];
    public minCpu: number | null = null;
    public minMemory: number | null = null;
    public minDisk: number | null = null;
    public recommendedCpu: number | null = null;
    public recommendedMemory: number | null = null;
    public recommendedDisk: number | null = null;
    public allowedNodeIds: string[] | null = null;
    public allowedLocationIds: string[] | null = null;
    public allowCrossLocationSwitch = false;

    public steamJoinButton: number | null = null;
    public steamJoinButtonPort = 0;

    // Extra allocations granted when provisioning a server of this egg, each an
    // offset from the primary or any free port, optionally bound to a variable.
    // The primary port range and any per-port range override live on the server
    // template, not the egg.
    public additionalPorts: AdditionalPort[] = [];
    // What happens when additionalPorts can't all be allocated.
    public additionalPortFailMode: AdditionalPortFailMode = 'continue';

    public sourceKey: string | null = null;
    public sourceId: string | null = null;
    public sourceLabel: string | null = null;
    public sourceUrl: string | null = null;
    public sourceViewUrl: string | null = null;
    public sourceHash: string | null = null;
    public sourceSyncedAt: string | null = null;
    public sourceDetached = false;
    public sourceUpdateAvailable = false;

    public exportData: { category?: string } | null = null;
    public canExport = false;

    getRouteName() {
        return 'egg';
    }

    getRouteID() {
        return this.id.toString();
    }

    public get tags(): Tag[] {
        return this.getRelationship('tags') || [];
    }
}

export enum EggSteamJoinButton {
    DISABLED = 0,
    GAME_PORT = 1,
    GAME_PORT_PLUS_N = 2,
    STATIC_PORT = 3,
}
